import { useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";

import { ApiError, api } from "@/api/client";
import { useAccountInvoices, useOrders, useVisits } from "@/api/commerce";
import { AiSummary } from "@/features/ai/AiPanels";
import { AccountService } from "@/features/service/AccountService";
import { crmKeys, useAccount, useContactsOf, useDeals, useDeleteAccount } from "@/api/crm";
import { useCountries, useFields, useMe } from "@/api/hooks";
import type { Account } from "@/api/types";
import { Badge, StatusBadge } from "@/components/crm/Badge";
import { formatCustom, useFieldLabels } from "@/components/crm/CustomFieldsFieldset";
import { findCountry } from "@/components/crm/LocationFields";
import { ActivityComposer, Timeline } from "@/components/crm/Timeline";
import { Alert, Avatar, Modal, PageHeader, Spinner, formatDate } from "@/components/ui";
import { ContactForm } from "@/features/contacts/ContactForm";
import { DealForm } from "@/features/deals/DealForm";
import { OrderStatusBadge, SYSTEM_NAMES } from "@/features/orders/badges";
import { money, shortDate, titleCase } from "@/lib/format";
import { can } from "@/lib/permissions";
import { AccountForm } from "./AccountForm";

/** Live EU VAT number check (VIES), kept on the account as proof for zero-rated supplies. */
function VatCheck({ account, canCheck }: { account: Account; canCheck: boolean }) {
  const qc = useQueryClient();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const v = account.vat_check;
  const check = async () => {
    setBusy(true); setError(null);
    try {
      const updated = await api<Account>("POST", `/accounts/${account.id}/vat-check`);
      qc.setQueryData(crmKeys.account(account.id), updated);
    } catch (e) { setError(e instanceof Error ? e.message : "Could not check"); } finally { setBusy(false); }
  };
  return (
    <div className="mt-4 flex flex-wrap items-center gap-3 border-t border-line pt-4 text-sm" data-testid="vat-check">
      {v ? (
        <span className={v.valid ? "text-ok" : "text-danger"}>
          {v.valid ? "Valid EU VAT number" : "Not a valid EU VAT number"} ({v.number}), checked with VIES {shortDate(v.checked_at)}
          {v.name && <span className="block text-xs text-muted">Registered to {v.name}{v.consultation && ` · consultation ${v.consultation}`}</span>}
        </span>
      ) : <span className="text-muted">Not checked with VIES yet.</span>}
      {canCheck && <button type="button" className="btn-ghost px-2 py-1 text-xs" disabled={busy} onClick={() => void check()}>{busy ? "Checking…" : "Check VAT number"}</button>}
      {error && <span className="w-full text-xs text-danger">{error}</span>}
    </div>
  );
}

export function AccountDetailPage() {
  const { id = "" } = useParams();
  const navigate = useNavigate();
  const perms = useMe().data?.permissions ?? [];
  const account = useAccount(id);
  const fields = useFields("account");
  const labels = useFieldLabels("account");
  const contacts = useContactsOf(id);
  const deals = useDeals({ account_id: id });
  const del = useDeleteAccount();
  const countries = useCountries();
  const canOrders = can(perms, "orders:read");
  const orders = useOrders({ account_id: id }, canOrders);
  const [editing, setEditing] = useState(false);
  const [adding, setAdding] = useState<"contact" | "deal" | null>(null);

  if (account.isLoading) return <Spinner />;
  if (account.error) {
    const notFound = account.error instanceof ApiError && account.error.status === 404;
    return <Alert>{notFound ? "This account does not exist or you do not have access to it." : account.error.message}</Alert>;
  }
  const a = account.data!;
  const openDeals = (deals.data?.items ?? []).filter((d) => d.status === "open");
  const pipelineValue = openDeals.reduce((sum, d) => sum + d.amount, 0);
  const currency = deals.data?.items[0]?.currency ?? "INR";

  const country = findCountry(countries.data, a.country);
  const details: [string, string | null][] = [
    ["Industry", a.industry], ["Phone", a.phone], ["Email", a.email], ["Website", a.website],
    [country?.tax_id_label ?? "Tax ID", a.tax_id], ["Parent account", a.parent?.name ?? null],
    ["Address", [a.address_line, a.city, a.state, a.postal_code, country?.name ?? a.country].filter(Boolean).join(", ") || null],
    ["Owner", a.owner?.full_name ?? null],
    ["Customer group", a.customer_group], ["Price book", a.price_book?.name ?? null], ["Territory", a.territory?.name ?? null],
    ["Name in accounts", a.ledger_name],
    ["Credit limit", a.credit_limit != null ? money(a.credit_limit, currency) : null],
    ...(fields.data ?? []).map((f): [string, string | null] => [f.label, formatCustom(a.custom[f.key])]),
  ];

  return (
    <>
      <Link to="/accounts" className="mb-4 inline-block text-sm text-muted hover:text-brand">← All accounts</Link>
      <PageHeader title={a.name} subtitle={[titleCase(a.account_type), a.city].filter(Boolean).join(" · ")}
        actions={<>
          {can(perms, "accounts:update") && <button type="button" className="btn-ghost" onClick={() => setEditing(true)}>Edit</button>}
          {can(perms, "accounts:delete") && <button type="button" className="btn-danger" onClick={() => {
            if (window.confirm(`Delete ${a.name}?`)) void del.mutateAsync(a.id).then(() => navigate("/accounts"));
          }}>Delete</button>}
        </>} />
      <div className="mb-6 grid gap-4 sm:grid-cols-3">
        <div className="card p-4"><p className="text-xs uppercase tracking-wide text-muted">Open deals</p><p className="mt-1 text-2xl font-semibold">{openDeals.length}</p></div>
        <div className="card p-4"><p className="text-xs uppercase tracking-wide text-muted">Open pipeline</p><p className="mt-1 text-2xl font-semibold">{money(pipelineValue, currency)}</p></div>
        {a.outstanding != null
          ? <div className="card p-4"><p className="text-xs uppercase tracking-wide text-muted">Outstanding</p><p className={`mt-1 text-2xl font-semibold ${a.outstanding > 0 ? "text-warn" : ""}`} data-testid="account-outstanding">{money(a.outstanding, currency)}</p>
              {a.outstanding_at && <p className="text-xs text-muted">From accounts, {shortDate(a.outstanding_at)}</p>}</div>
          : <div className="card p-4"><p className="text-xs uppercase tracking-wide text-muted">People</p><p className="mt-1 text-2xl font-semibold">{contacts.data?.items.length ?? 0}</p></div>}
      </div>
      <div className="grid gap-6 lg:grid-cols-3">
        <div className="min-w-0 space-y-6 lg:col-span-2">
          <section className="card p-6" aria-label="Details">
            <dl className="grid gap-x-6 gap-y-4 sm:grid-cols-2">
              {details.map(([label, value]) => (
                <div key={label}><dt className="text-xs font-medium uppercase tracking-wide text-muted">{label}</dt><dd className="mt-0.5 break-words text-sm">{value ?? <span className="text-muted">—</span>}</dd></div>
              ))}
            </dl>
            {country?.region === "EU" && a.tax_id && <VatCheck account={a} canCheck={can(perms, "accounts:update")} />}
          </section>
          <section className="card" aria-label="Deals">
            <div className="flex items-center justify-between border-b border-line px-5 py-3">
              <h2 className="font-semibold">Deals</h2>
              {can(perms, "deals:create") && <button type="button" className="btn-ghost text-xs" onClick={() => setAdding("deal")}>Add deal</button>}
            </div>
            {deals.data?.items.length ? (
              <ul className="divide-y divide-line">
                {deals.data.items.map((d) => (
                  <li key={d.id} className="flex items-center justify-between gap-3 px-5 py-3">
                    <Link to={`/deals/${d.id}`} className="min-w-0 font-medium hover:text-brand"><span className="block truncate">{d.name}</span><span className="text-xs font-normal text-muted">{d.stage.name}</span></Link>
                    <span className="flex shrink-0 items-center gap-3 text-sm">{money(d.amount, d.currency)}<StatusBadge status={d.status} /></span>
                  </li>
                ))}
              </ul>
            ) : <p className="px-5 py-4 text-sm text-muted">No deals yet.</p>}
          </section>
          {canOrders && (
            <section className="card" aria-label="Orders">
              <h2 className="border-b border-line px-5 py-3 font-semibold">Sales orders</h2>
              {orders.data?.items.length ? (
                <ul className="divide-y divide-line">
                  {orders.data.items.map((o) => (
                    <li key={o.id} className="flex items-center justify-between gap-3 px-5 py-3">
                      <Link to={`/orders/${o.id}`} className="font-medium hover:text-brand">{o.number}<span className="block text-xs font-normal text-muted">{shortDate(o.order_date)}{o.customer_po && ` · PO ${o.customer_po}`}</span></Link>
                      <span className="flex items-center gap-3 text-sm">{money(o.grand_total, o.currency)}<OrderStatusBadge status={o.status} /></span>
                    </li>
                  ))}
                </ul>
              ) : <p className="px-5 py-4 text-sm text-muted">No orders yet.</p>}
            </section>
          )}
          {canOrders && <InvoicesSection accountId={a.id} />}
          {can(perms, "visits:read") && <VisitsSection accountId={a.id} />}
          <AccountService account={{ id: a.id, name: a.name }} />
          <section className="card" aria-label="People">
            <div className="flex items-center justify-between border-b border-line px-5 py-3">
              <h2 className="font-semibold">People</h2>
              {can(perms, "contacts:create") && <button type="button" className="btn-ghost text-xs" onClick={() => setAdding("contact")}>Add person</button>}
            </div>
            {contacts.data?.items.length ? (
              <ul className="divide-y divide-line">
                {contacts.data.items.map((c) => (
                  <li key={c.id} className="flex items-center gap-3 px-5 py-3">
                    <Avatar name={c.full_name} />
                    <Link to={`/contacts/${c.id}`} className="min-w-0 hover:text-brand"><span className="block font-medium">{c.full_name}</span><span className="break-all text-xs text-muted">{[c.job_title, c.email, c.phone].filter(Boolean).join(" · ")}</span></Link>
                  </li>
                ))}
              </ul>
            ) : <p className="px-5 py-4 text-sm text-muted">No people linked yet.</p>}
          </section>
        </div>
        <div className="min-w-0 space-y-6">
        <AiSummary objectType="account" id={a.id} />
        <section className="card min-w-0 space-y-5 p-5" aria-label="Activity">
          <h2 className="font-semibold">Activity</h2>
          {can(perms, "activities:create") && <ActivityComposer target={{ type: "account", id: a.id }} />}
          <Timeline target={{ type: "account", id: a.id }} labels={labels} />
        </section>
        </div>
      </div>
      {editing && <Modal title={`Edit ${a.name}`} onClose={() => setEditing(false)}><AccountForm account={a} onCancel={() => setEditing(false)} onSaved={() => setEditing(false)} /></Modal>}
      {adding === "contact" && <Modal title="Add person" onClose={() => setAdding(null)}><ContactForm defaultAccount={{ id: a.id, name: a.name }} onCancel={() => setAdding(null)} onSaved={() => setAdding(null)} /></Modal>}
      {adding === "deal" && <Modal title="New deal" onClose={() => setAdding(null)}><DealForm defaultAccount={{ id: a.id, name: a.name }} onCancel={() => setAdding(null)} onSaved={() => setAdding(null)} /></Modal>}
    </>
  );
}

function InvoicesSection({ accountId }: { accountId: string }) {
  const invoices = useAccountInvoices(accountId);
  if (!invoices.data?.length) return null;
  return (
    <section className="card overflow-x-auto" aria-label="Invoices">
      <h2 className="border-b border-line px-5 py-3 font-semibold">Invoices</h2>
      <table className="w-full text-left text-sm">
        <tbody>
          {invoices.data.map((i) => (
            <tr key={i.id} className="border-b border-line last:border-0">
              <td className="px-5 py-2.5"><span className="font-medium">{i.number}</span><span className="block text-xs text-muted">{shortDate(i.invoice_date)}{i.order_number && ` · order ${i.order_number}`} · {SYSTEM_NAMES[i.source]}</span></td>
              <td className="px-2 py-2.5 text-right tabular-nums">{money(i.amount, i.currency)}</td>
              <td className="px-5 py-2.5 text-right">{i.balance > 0 ? <Badge tone={i.overdue ? "danger" : "warn"}>{money(i.balance, i.currency)} {i.overdue ? "overdue" : "unpaid"}</Badge> : <Badge tone="ok">Paid</Badge>}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </section>
  );
}

function VisitsSection({ accountId }: { accountId: string }) {
  const visits = useVisits({ account_id: accountId, mine: false });
  if (!visits.data?.length) return null;
  return (
    <section className="card" aria-label="Visits">
      <h2 className="border-b border-line px-5 py-3 font-semibold">Field visits</h2>
      <ul className="divide-y divide-line">
        {visits.data.slice(0, 10).map((v) => (
          <li key={v.id} className="px-5 py-2.5 text-sm">
            <span className="font-medium">{v.purpose}</span>
            <span className="block text-xs text-muted">{[v.owner?.full_name, v.check_in_at && formatDate(v.check_in_at), v.distance_m != null && `${v.distance_m} m from the site`, v.status.replace("_", " ")].filter(Boolean).join(" · ")}</span>
          </li>
        ))}
      </ul>
    </section>
  );
}
